/**
 * EL BOTÓN «AYUDA» DE CADA PANTALLA (2026-10-26).
 *
 * Lo pidió el dueño: «en las pantallas pon un botón de ayuda en una esquina
 * superior; al darle se abre una ventana con una explicación y el video
 * tutorial, si es que hicimos uno». Va arriba a la derecha, el último de los
 * botones del encabezado, en todas las pantallas que tienen ayuda.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  SE PONE SOLO EN `EncabezadoPagina`
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Las pantallas que usan `EncabezadoPagina` (components/ui.tsx) lo llevan sin
 * escribir nada: el encabezado mira la dirección y, si hay ayuda, lo dibuja.
 * Las que arman su encabezado a mano (Vender, Ventas, Catálogo…) lo ponen con
 * `<BotonDeAyuda />` junto a sus botones. Sin `tema`, también mira la
 * dirección.
 *
 * ⚠️ Este archivo NO importa nada de `components/ui.tsx`: `ui.tsx` lo importa
 * a él, y el círculo rompería la carga. Por eso las clases van escritas aquí.
 *
 * La ventana se descarga al abrirla (`lazy`): con ella vienen los textos de
 * todas las pantallas, y nadie tiene por qué esperarlos para vender.
 */

import { lazy, Suspense, useState } from "react";
import { useInRouterContext, useLocation } from "react-router-dom";
import { CircleHelp } from "lucide-react";
import { esTrozoQueNoLlego, recargarPorVersionNueva } from "@/lib/recargaPorVersion";
import { enNegocios, temaDeLaRuta } from "./rutas";

const VentanaDeAyuda = lazy(async () => {
  try {
    return await import("./VentanaDeAyuda");
  } catch (error) {
    // La ventana es de una versión que ya no está en el servidor: se recarga
    // una vez, como las pantallas (ver `perezosa` en router.tsx).
    if (esTrozoQueNoLlego(error) && recargarPorVersionNueva()) {
      return new Promise<never>(() => {});
    }
    throw error;
  }
});

type Props = {
  tema?: string;
  className?: string;
  /**
   * El de la barra de arriba del celular: solo el icono, como la campana.
   *
   * ⚠️ EN EL CELULAR LA AYUDA VA ARRIBA, NO EN EL ENCABEZADO DE LA PANTALLA.
   * En 375 píxeles el título y sus botones ya llenan la fila, y la «?» caía
   * sola en una segunda línea, debajo del título. En la barra superior está
   * siempre en el mismo sitio, junto a la campana, en todas las pantallas.
   * Por eso el del encabezado se esconde por debajo de `lg` (la misma medida
   * en que aparece esa barra).
   */
  compacto?: boolean;
};

export function BotonDeAyuda(props: Props) {
  // Sin enrutador no hay dirección que mirar (una prueba que dibuja una
  // pantalla suelta): sin botón, en vez de romper la pantalla entera.
  return useInRouterContext() ? <BotonEnLaRuta {...props} /> : null;
}

function BotonEnLaRuta({ tema, className, compacto = false }: Props) {
  const { pathname } = useLocation();
  const [abierta, setAbierta] = useState(false);

  const id = tema ?? temaDeLaRuta(pathname);
  if (!id) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setAbierta(true)}
        aria-label="Ayuda de esta pantalla"
        title="Ayuda de esta pantalla"
        className={(compacto
          ? [
              // Igual que la campana de al lado.
              "shrink-0 rounded-[var(--radius-control)] p-2.5 text-muted transition-colors hover:bg-surface-hover hover:text-ink",
            ]
          : [
              // `min-h-9` y no `h-9`: al lado de botones más altos se estira a
              // su altura (el contenedor es flex) y la fila queda pareja.
              "pulsable hidden min-h-9 shrink-0 items-center justify-center gap-1.5 rounded-[var(--radius-control)] px-3 lg:inline-flex",
              "border border-line-strong bg-surface text-sm font-medium text-ink-soft",
              "transition-colors hover:border-accent/50 hover:bg-surface-hover hover:text-ink",
            ]
        )
          .concat("focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40", className ?? "")
          .join(" ")}
      >
        {compacto ? (
          <CircleHelp size={18} />
        ) : (
          <>
            <CircleHelp size={17} className="text-accent" />
            Ayuda
          </>
        )}
      </button>

      {abierta && (
        <Suspense fallback={null}>
          <VentanaDeAyuda tema={id} enNegocios={enNegocios(pathname)} onCerrar={() => setAbierta(false)} />
        </Suspense>
      )}
    </>
  );
}
